import { format } from 'date-fns';
import { Check } from 'lucide-react-native';
import type { IMessage, BubbleProps } from 'react-native-gifted-chat';

import { ChatCustomView, type CustomBubbleProps, type ICustomMessage } from '@/components/chat';
import { OpeningBubbleMeasure } from '@/components/chat/bubbles/OpeningBubbleMeasure';
import { PrimumSummarySection } from '@/components/chat/bubbles/PrimumSummarySection';
import { ExtractedQuestionsBubbleProvider } from '@/components/chat/bubbles/extractedQuestions/ExtractedQuestionsBubbleContext';
import { HighlightedMessageText } from '@/components/chat/bubbles/extractedQuestions/HighlightedMessageText';
import { QuestionCardsList } from '@/components/chat/bubbles/extractedQuestions/QuestionCardsList';
import { ChatBubble } from '@/components/chat/message-list/ChatBubble';
import { Box } from '@/components/ui/box';
import { HStack } from '@/components/ui/hstack';
import { Icon } from '@/components/ui/icon';
import { Text } from '@/components/ui/text';
import { VStack } from '@/components/ui/vstack';
import { useRecommendedCaseLayout } from '@/hooks/chat/useRecommendedCaseLayout';
import type { useColors } from '@/hooks/useColors';
import type { ClinicalCase } from '@/types/clinicalCase';
import type { ExtractedQuestion } from '@/types/extractedQuestions';

type Colors = ReturnType<typeof useColors>['colors'];

interface ExtractedQuestionsBubbleProps {
  bubbleProps: CustomBubbleProps;
  colors: Colors;
  closedConversation: boolean;
  isAuthor: boolean;
  conversationId: string;
  userId: string;
  /** Constrain the bubble to its content width instead of filling the row.
   * Set when a caption sits above it: a caption pairs its label with a
   * content-width bubble, and a full-width one stretches the column. */
  shrinkWidthToContent?: boolean;
  /** Set on the opening message only: the submitter's case, shown above the question cards. */
  clinicalCase?: ClinicalCase;
}

/**
 * Bubble variant for LMP messages whose attributes include a non-empty
 * `extractedQuestions` array. Renders the standard text bubble chrome with
 * an inline-highlighted body, the timestamp row, and the question cards
 * below it separated by a subtle horizontal rule. The cards are always
 * shown: they are the point of the bubble, and a toggle only hid them.
 *
 * `ExtractedQuestionsBubbleProvider` calls the `useExtractedQuestionsBubble`
 * hook once and hands its pulse callbacks to the subtree via React context.
 */
export function ExtractedQuestionsBubble({
  bubbleProps,
  colors,
  closedConversation,
  isAuthor,
  conversationId,
  userId,
  shrinkWidthToContent = false,
  clinicalCase,
}: ExtractedQuestionsBubbleProps) {
  const isRecommendedLayout = useRecommendedCaseLayout(clinicalCase);

  const message = bubbleProps.currentMessage as ICustomMessage;

  const messageSid = message.messageId;

  const extractedQuestions = message.extractedQuestions ?? [];

  const primaryQuestionIndex = message.primaryQuestionIndex ?? -1;

  const position: 'left' | 'right' = bubbleProps.position ?? 'left';

  return (
    <ExtractedQuestionsBubbleProvider
      messageSid={messageSid}
      questionCount={extractedQuestions.length}>
      <OpeningBubbleMeasure conversationId={conversationId} enabled={!!clinicalCase}>
        <ChatBubble
          fillRow={!shrinkWidthToContent}
          outlined={isRecommendedLayout}
          shrinkWidthToContent={shrinkWidthToContent}
          bubbleProps={{
            ...bubbleProps,
            renderMessageText: (textProps: BubbleProps<IMessage>) => {
              const text = (textProps.currentMessage as ICustomMessage)?.text ?? '';

              const messageTextColorClassName =
                position === 'right' ? 'text-typography-white' : 'text-typography-900';

              return (
                <Box className="px-3 pt-2">
                  <HighlightedMessageText
                    messageSid={messageSid}
                    body={text}
                    extractedQuestions={extractedQuestions}
                    textClassName={`${messageTextColorClassName} leading-snug`}
                    position={position}
                    recommended={isRecommendedLayout}
                  />
                </Box>
              );
            },
            renderTime: (timeProps: BubbleProps<IMessage>) => (
              <BubbleBottomSlot
                extractedQuestions={extractedQuestions}
                primaryQuestionIndex={primaryQuestionIndex}
                position={position}
                currentMessage={timeProps.currentMessage as ICustomMessage}
                conversationId={conversationId}
                clinicalCase={clinicalCase}
              />
            ),
          }}
          colors={colors}
          renderCustomContent={(customViewProps) => {
            const props = customViewProps as CustomBubbleProps;

            return (
              <Box className="px-3">
                <ChatCustomView
                  {...props}
                  closedConversation={closedConversation}
                  isAuthor={isAuthor}
                  conversationId={conversationId}
                  userId={userId}
                />
              </Box>
            );
          }}
        />
      </OpeningBubbleMeasure>
    </ExtractedQuestionsBubbleProvider>
  );
}

interface BubbleBottomSlotProps {
  extractedQuestions: ExtractedQuestion[];
  primaryQuestionIndex: number;
  position: 'left' | 'right';
  currentMessage: ICustomMessage | undefined;
  conversationId: string;
  clinicalCase?: ClinicalCase;
}

/**
 * Renders the bottom container of the bubble: the timestamp + delivery ticks
 * on the right, then the "Summarized by Primum" section with the case summary
 * (opening message only) and the question cards under it.
 *
 * Lives in GiftedChat's `renderTime` slot so it sits at the very bottom of
 * the bubble after the message body and any custom view.
 */
function BubbleBottomSlot({
  extractedQuestions,
  primaryQuestionIndex,
  position,
  currentMessage,
  conversationId,
  clinicalCase,
}: BubbleBottomSlotProps) {
  const isRecommendedLayout = useRecommendedCaseLayout(clinicalCase);

  if (!currentMessage) {
    return null;
  }

  const { createdAt, received, sent } = currentMessage;

  const timeColorClassName = position === 'left' ? 'text-typography-400' : 'text-typography-white';

  return (
    <VStack className="w-full pt-2">
      <HStack space="sm" className="items-center justify-end">
        <HStack space="xs" className="items-center">
          <Text size="2xs" className={timeColorClassName}>
            {createdAt ? format(createdAt, 'p') : ''}
          </Text>
          <HStack>
            {[received, sent].filter(Boolean).map((_, index) => (
              <Icon size="xs" key={index} as={Check} className={timeColorClassName} />
            ))}
          </HStack>
        </HStack>
      </HStack>
      <PrimumSummarySection
        position={position}
        conversationId={conversationId}
        clinicalCase={clinicalCase}>
        <QuestionCardsList
          extractedQuestions={extractedQuestions}
          primaryQuestionIndex={primaryQuestionIndex}
          position={position}
          hanging={isRecommendedLayout}
        />
      </PrimumSummarySection>
    </VStack>
  );
}
